"use client";

import React, { useEffect, useRef } from "react";
import { ClusterApiResponse } from "@/components/PageCluster/api/interface";
import { DeviceInfo } from "@/interface/deviceInfo";
import { useSyncClusterData } from "@/components/PageCluster/helpers/useSyncClusterData";
import { generateBreadcrumbs } from "@/components/PageCluster/helpers/generateBreadcrumbs";
import Breadcrumbs from "@/components/Breadcrumbs";
import ComponentMapping from "@/components/PageCluster/components/ComponentMapping";
import { COMMON_COMPONENT_TYPES, EVENT_NAMES, NON_ONBOARDED_STATUS, ONBOARDING_STATUS } from "@/helpers/constants";
import { RefsObjectType, RegisterRefType } from "@/components/PageHome/interfaces";
import NavBar from "@/components/Header/NavBar";
import routes from "@/helpers/router";
import { ProductInfo } from "@/components/ShoppingComponent/types/productInfo";
import { DoctorDetails } from "@/redux/slice/doctors/types/FetchDoctorsPayload";
import signedInStateSelector from "@/redux/slice/auth/selectors/signedInState";
import currentPatientSelector from "@/redux/slice/auth/selectors/currentPatient";
import { useAppSelector } from "@/redux/hooks";
import { SignedinState } from "@/redux/slice/auth/enums/SignedInState";
import clevertapTracking from "@/libs/clevertapTracking";
import { pageViewEvent } from "@/helpers/TSDEvents";
import { DiagnosticDetailInterface } from "@/redux/slice/diagnostics/types/fetchDiagnosticsPayload";
import styles from "./PageCluster.module.scss";

interface ClusterPageComponentProps {
  ua: DeviceInfo;
  clusterData: ClusterApiResponse;
  clusterSlug: string;
  userOnBoarded: boolean;
  productInfo?: ProductInfo;
  currentSpeciality: string;
  doctorsArray: DoctorDetails[];
  segmentInfoCookie?: any;
  diagnosticsArray?: DiagnosticDetailInterface[];
}

const ClusterPageComponent: React.FC<ClusterPageComponentProps> = (props) => {
  const {
    ua,
    clusterData,
    clusterSlug,
    userOnBoarded,
    productInfo,
    currentSpeciality,
    doctorsArray,
    segmentInfoCookie,
    diagnosticsArray,
  } = props;

  const segmentInformation = segmentInfoCookie ? JSON.parse(segmentInfoCookie?.value) : null;

  useSyncClusterData({ clusterData, ua, clusterSlug });

  const signedInStatus = useAppSelector(signedInStateSelector);
  const currentPatientData = useAppSelector(currentPatientSelector);

  const elementRefs = useRef<RefsObjectType>({});

  const registerRef: RegisterRefType = (key, element) => {
    elementRefs.current[key] = element;
  };

  useEffect(() => {
    if (signedInStatus !== SignedinState.UNRESOLVED) {
      const isUserLoggedin = signedInStatus === SignedinState.SIGNEDIN;
      pageViewEvent({
        page: "cluster",
        is_loggedin: isUserLoggedin ? true : false,
        momverse_stage: userOnBoarded ? (ONBOARDING_STATUS[segmentInformation?.type] ?? "") : NON_ONBOARDED_STATUS,
        firstname: isUserLoggedin && currentPatientData?.firstName ? currentPatientData?.firstName : "",
        page_name: `Cluster Page: ${clusterData?.data?.[0]?.attributes?.Name} `,
      });
    }
  }, [signedInStatus]);

  const clusterDetails = clusterData?.data?.[0]?.attributes;
  const breadCrumbsArray = generateBreadcrumbs(clusterDetails);
  const clusterSections = clusterDetails?.ClusterContent?.map((section: any, index: number) => ({
    ...section,
    refId: `${index}`,
  }));

  const topPanelSections = clusterSections?.filter((section: any) => section?.Position === "Top");
  const rightPanelSections = clusterSections?.filter(
    (section: any) => section?.Position !== "Left" && section?.Position !== "Top" && section?.Position !== "Bottom"
  );
  const bottomPanelSections = clusterSections?.filter((section: any) => section?.Position === "Bottom");
  const leftPanelSections = clusterSections?.filter((section: any) => section?.Position === "Left");

  const fullWidthComponentTypes = [
    ...bottomPanelSections?.map((section: any) => section.ComponentType),
    ...topPanelSections?.map((section: any) => section.ComponentType),
  ];
  const navBarInfo = clusterSections?.find((section) => section.ComponentType === COMMON_COMPONENT_TYPES.NavBar);

  const extraData = {
    indexContent: clusterSections?.filter((section: any) => section?.ShowHeadingInIndex),
    elementRefs,
    registerRef,
    productInfo,
    currentSpeciality,
    doctorsArray,
    diagnostics: diagnosticsArray,
  };

  return (
    <>
      {navBarInfo && (
        <NavBar
          navBarData={navBarInfo}
          userOnBoarded={userOnBoarded}
          userAgent={ua}
          pathname={routes.cluster(clusterSlug)}
        />
      )}
      <div className={`breadcrumb container`}>
        <Breadcrumbs options={breadCrumbsArray} />
      </div>

      {ua?.isMobile && (
        <div className={`${styles.clusterInsideSection}`}>
          <>
            {clusterSections?.map((section: any, index: number) => {
              return (
                <React.Fragment key={index}>
                  <div
                    className={`${fullWidthComponentTypes?.includes(section.ComponentType) ? `` : `container ${styles.clusterPageRoot}`}`}
                  >
                    <ComponentMapping
                      data={section}
                      userOnBoarded={userOnBoarded}
                      clusterSlug={clusterSlug}
                      extraData={extraData}
                    />
                  </div>
                </React.Fragment>
              );
            })}
          </>
        </div>
      )}

      {ua?.isDesktop && (
        <>
          {topPanelSections?.map((section: any, index: number) => {
            return (
              <React.Fragment key={`top_${index}`}>
                <ComponentMapping data={section} clusterSlug={clusterSlug} extraData={extraData} />
              </React.Fragment>
            );
          })}
          <div className={`${styles.columnsContainer} container ${styles.clusterPageRoot}`}>
            <div className={styles.leftSection}>
              {leftPanelSections?.map((section: any, index: number) => {
                return (
                  <React.Fragment key={`left_${index}`}>
                    <ComponentMapping data={section} clusterSlug={clusterSlug} extraData={extraData} />
                  </React.Fragment>
                );
              })}
            </div>
            <div className={styles.rightSection}>
              {rightPanelSections?.map((section: any, index: number) => {
                return (
                  <React.Fragment key={`right_${index}`}>
                    <ComponentMapping data={section} clusterSlug={clusterSlug} extraData={extraData} index={index} />
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div>
            {bottomPanelSections?.map((section: any, index: number) => {
              return (
                <React.Fragment key={`bottom_${index}`}>
                  <ComponentMapping data={section} clusterSlug={clusterSlug} extraData={extraData} />
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}
    </>
  );
};

export default ClusterPageComponent;
